// MediaLibrary.tsx

import { useContext, useEffect, useRef, useState } from "react";
import {
    Button,
    Modal,
    Spinner,
    TextField,
    Popover,
    ActionList,
    Icon,
    Checkbox,
    RadioButton,
    Filters,
    ChoiceList,
} from "@shopify/polaris";
import { endPoints, getData, postData } from "../../utilities/networkCalls";
import {
    CheckIcon,
    ListBulletedIcon,
    SearchIcon,
    SortIcon,
    TextInColumnsIcon,
    ViewIcon,
    XCircleIcon,
} from "@shopify/polaris-icons";
import styles from "./MediaLibrary.module.css";
import { AppConfigContext } from "../../context/AppConfigContext";
import { fetchProductsWithCursor } from "../../apps/Bundles/constants";
import useIsMobile from "../../hooks/useIsMobile";

export type MediaLibraryProps = {
    open: boolean;
    onClose: () => void;
    onSelect: (selectedItems: any[]) => void;
    multiple?: boolean;
    restrictToImages?: boolean;
    initialSelectedItems?: any[]; // to pre-fill selection
    title?: string; // customizable modal title
    mediaItems?: any[]; // parent can pass media if needed
    selectButtonLabel?: string; // customize Select button text
};

type MediaTypeFilter = "Image" | "Video" | "ExternalVideo" | "Model3d";

export interface FilterPayload {
    after: string | null;
    sortKey: "ID" | "FILENAME" | "ORIGINAL_UPLOAD_SIZE";
    reverse: "false" | "true";
    mediaType?: MediaTypeFilter[] | null;
    products?: ProductOption[] | null;
    status?: string | null;
    originalUploadSize?: { min?: string; max?: string };
    usedIn?: "none" | "product" | null;
    title: string | null;
}

type FileOperationPayload = {
    operationType: "UPLOAD_POLLING" | "UPDATE_ALT_TEXT";
    ids: string[];
    altText?: string[];
};

export type BaseFileItem = {
    __typename: string; // will be narrowed later
    id: string;
    createdAt: string;
    alt: string;
    fileStatus: string;
    preview: {
        status: string;
        image: {
            id: string;
            width: number;
            height: number;
            url: string;
            transformedSrc: string;
            __typename: string;
        };
        __typename: string;
    };
};
type ProductOption = {
    title: string;
    id: string;
};
type Preview = {
    file: FileItem;
    fileName: string;
    fileType: string;
    biggerPreviewUrl: string;
};

// MediaImage
export type MediaImageItem = BaseFileItem & {
    __typename: "MediaImage";
    mimeType: string;
    originalSource: {
        url: string;
    };
};

// Video
export type VideoItem = BaseFileItem & {
    __typename: "Video";
    filename: string;
    duration: number;
    originalSource: {
        url: string;
        format: string;
    };
};

// ExternalVideo
export type ExternalVideoItem = BaseFileItem & {
    __typename: "ExternalVideo";
    originUrl: string;
};

// Model3d
export type Model3dItem = BaseFileItem & {
    __typename: "Model3d";
    originalSource: {
        url: string;
        format: string;
    };
};

// Final union type
export type FileItem = MediaImageItem | VideoItem | ExternalVideoItem | Model3dItem;

const mediaTypeLabels: Record<string, string> = {
    Image: "Images",
    Video: "Videos",
    ExternalVideo: "External Videos",
    Model3d: "3D Models",
};
// default filter payload
export const defaultFilterPayload: FilterPayload = {
    after: null,
    sortKey: "ID",
    reverse: "true",
    mediaType: [],
    originalUploadSize: { min: "", max: "" },
    products: [],
    status: null,
    usedIn: null,
    title: "",
};

const MediaLibrary = ({
    open,
    onClose,
    onSelect,
    multiple = true,
    restrictToImages = false,
    initialSelectedItems = [],
    title = "Media Library",
    mediaItems = [],
    selectButtonLabel = "Select",
}: MediaLibraryProps) => {
    const { sft } = useContext(AppConfigContext);
    const [files, setFiles] = useState<FileItem[]>([]);
    const firstPageRender = useRef(true);
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const scrollContainer = useRef<HTMLDivElement | null>(null);
    const [fetchingFiles, setFetchingFiles] = useState(false);
    const [filtering, setFiltering] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<{ fileId: string; previewUrl: string }[]>([]);
    const [filterPayload, setFilterPayload] = useState(defaultFilterPayload);
    const [searchInputValue, setSearchInputValue] = useState(defaultFilterPayload.title || "");
    const [productSearchTerm, setProductSearchTerm] = useState("");

    const [cursor, setCursor] = useState<string | null>(null);
    const [filesHasNextPage, setFilesHasNextPage] = useState(true);
    const [productCursor, setproductCursor] = useState<string | null>(null);
    const [productHasNextPage, setProductHasNextPage] = useState(true);
    const lastSearchTermRef = useRef<string>("");
    const [productOptions, setProductOptions] = useState<ProductOption[]>([]);

    const [sortPopoverActive, setSortPopoverActive] = useState(false);
    const [layoutTypePopoverActive, setLayoutTypePopoverActive] = useState(false);
    const [layoutType, setLayoutType] = useState<"list" | "grid">("list");

    const toggleSortPopover = () => setSortPopoverActive((active) => !active);
    const toggleLayoutTypePopoverActive = () => setLayoutTypePopoverActive((active) => !active);

    const [previewFile, setPreviewFile] = useState<Preview | null>(null);
    const searchDebounceRef = useRef<any>(null);
    const { isMobile } = useIsMobile();

    const handleFileOperation = async () => {
        const payload: FileOperationPayload = {
            operationType: "UPDATE_ALT_TEXT",
            ids: [],
            altText: ["first alt text"],
        };
        const res = await postData({
            url: endPoints.filesOperation,
            payload: payload,
        });

        if (res.status === 200 && res.data) {
            if (payload.operationType === "UPLOAD_POLLING") {
                const updatedStatuses = res.data.filesStatus || [];
                // Update each file in state by matching id
                setFiles((prevFiles) =>
                    prevFiles.map((file) => {
                        const updatedFile = updatedStatuses.find((f: any) => f.id === file.id);
                        return updatedFile ? { ...file, ...updatedFile } : file;
                    }),
                );
            } else if (payload.operationType === "UPDATE_ALT_TEXT") {
                const updatedFile = res.data.updatedFile;
                setFiles((prev) => prev.map((f) => (f.id === updatedFile.id ? { ...f, alt: updatedFile.alt } : f)));
            }
        }
    };

    const handleFetchImages = async (after: string | null = null, reset = false) => {
        if (!filesHasNextPage && !reset) return; // allow fetch if reset=true even if hasNextPage=false

        if (reset) {
            setFiltering(true);
        } else {
            setFetchingFiles(true);
        }

        // if reset true clear cursor and hasNextPage first
        if (reset) {
            setCursor(null);
            setFilesHasNextPage(true);
        }

        const params: FilterPayload = {
            ...filterPayload,
            after, // pass after in params
            ...(restrictToImages && { mediaType: ["Image"] }),
        };

        const res = await getData({
            url: endPoints.getfiles,
            params,
        });

        if (res.status === 200 && res.data && res.data.files) {
            const newFiles = res.data.files;
            setFiles((prev) => (reset ? newFiles : [...prev, ...newFiles]));
            setFilesHasNextPage(res.data.pageInfo?.hasNextPage);
            setCursor(res.data.pageInfo?.endCursor || null);
        }

        if (reset) {
            setFiltering(false);
        } else {
            setFetchingFiles(false);
        }

        if (firstPageRender.current) {
            firstPageRender.current = false;
        }
    };

    useEffect(() => {
        const trigger = loadMoreTriggerRef.current;
        const container = scrollContainer.current;
        const isBlocked = fetchingFiles || filtering;

        console._log("observer setup → trigger:", trigger, "root:", container);

        if (!trigger || !container || !filesHasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isBlocked) {
                    observer.disconnect();
                    handleFetchImages(cursor);
                }
            },
            {
                root: null,
                threshold: 0.2,
            },
        );

        observer.observe(trigger);

        return () => {
            observer.unobserve(trigger);
            observer.disconnect();
        };
    }, [filesHasNextPage, cursor]);

    const handleClose = () => {
        firstPageRender.current = true;
        setCursor(null);
        setProductHasNextPage(true);
        setFilesHasNextPage(true);
        setproductCursor(null);
        setProductOptions([]);
        setPreviewFile(null);

        setFiles([]);
        onClose();
    };

    const handleSelect = () => {
        firstPageRender.current = true;
        setCursor(null);
        setProductHasNextPage(true);
        setFilesHasNextPage(true);
        setproductCursor(null);
        setProductOptions([]);
        setPreviewFile(null);

        setFiles([]);
        onSelect(selectedFiles);
    };

    const fetchMoreProducts = async (searchTerm: string = "") => {
        const isNewSearch = lastSearchTermRef.current !== searchTerm;

        // only block when no more products + no new search term (normal infinite scroll)
        if (!productHasNextPage && !isNewSearch) return;

        //  if new search then reset cursor and productOptions
        if (isNewSearch) {
            setProductOptions([]);
            setproductCursor(null); // reset cursor
            setProductHasNextPage(true); //reset hasnextpage
        }

        // pass correct cursor based on new search or old
        const currentCursor = isNewSearch ? null : productCursor;

        const { products, pageInfo } = await fetchProductsWithCursor(sft, currentCursor, 15, searchTerm);

        setProductOptions((prev) => {
            const finalarray = isNewSearch ? [...products] : [...prev, ...products];
            console._log("prev and new :", prev, products, finalarray);
            return finalarray; // this is enough
        });

        setProductHasNextPage(pageInfo?.hasNextPage);
        setproductCursor(pageInfo?.hasNextPage ? pageInfo?.endCursor : null);

        // update ref with current search term
        lastSearchTermRef.current = searchTerm;
    };
    const handleToggleChecked = (fileId: string, previewUrl: string) => {
        setSelectedFiles((prev) => {
            if (multiple) {
                // multiple select: store both fileId and previewUrl in the array
                const exists = prev.some((item) => item.fileId === fileId);
                if (exists) {
                    return prev.filter((item) => item.fileId !== fileId); // remove if already selected
                } else {
                    return [...prev, { fileId, previewUrl }]; // add new fileId and previewUrl
                }
            } else {
                // single select: reset to only the selected fileId and previewUrl
                if (prev.length > 0 && prev[0].fileId === fileId) {
                    return []; // clear selection if the same file is clicked again
                } else {
                    return [{ fileId, previewUrl }]; // set the selected file with previewUrl
                }
            }
        });
    };

    const isChecked = (fileId: string) => selectedFiles.some((file) => file.fileId === fileId);

    const handlePreview = ({ file, fileName, fileType, biggerPreviewUrl }: Preview) => {
        setPreviewFile({ file, fileName, fileType, biggerPreviewUrl });
    };

    const getAppliedFilters = () => {
        const applied = [];

        if (filterPayload.mediaType && filterPayload.mediaType.length > 0) {
            applied.push({
                key: "fileType",
                label: `${
                    filterPayload.mediaType
                        .slice(0, 3)
                        .map((type) => mediaTypeLabels[type] || type)
                        .join(", ") + (filterPayload.mediaType.length > 3 ? ", ..." : "")
                }`,
                onRemove: () =>
                    setFilterPayload((prev) => ({
                        ...prev,
                        mediaType: restrictToImages ? ["Image"] : [],
                    })),
            });
        }
        if (filterPayload.originalUploadSize) {
            const { min, max } = filterPayload.originalUploadSize;
            if ((min && min !== "") || (max && max !== "")) {
                let label = "";
                if (min && min !== "" && (!max || max === "")) {
                    label = `File size: Greater than ${min} MB`; // only min
                } else if ((!min || min === "") && max && max !== "") {
                    label = `File size: Less than ${max} MB`; // only max
                } else if (min && max && min !== "" && max !== "") {
                    label = `File size: ${min} - ${max} MB`; // both
                }
                applied.push({
                    key: "fileSize",
                    label,
                    onRemove: () =>
                        setFilterPayload((prev) => ({
                            ...prev,
                            originalUploadSize: { min: "", max: "" },
                        })),
                });
            }
        }

        if (filterPayload.usedIn) {
            applied.push({
                key: "usedIn",
                label: `${filterPayload.usedIn === "product" ? "Product media" : "Other media"}`,
                onRemove: () => setFilterPayload((prev) => ({ ...prev, usedIn: null })),
            });
        }

        if (filterPayload.products && filterPayload.products.length > 0 && filterPayload.products[0].id !== "") {
            const maxTitles = 2;
            const maxChars = 50;

            const selectedTitles = filterPayload.products.slice(0, maxTitles).map((p) => p.title);
            let joinedTitles = selectedTitles.join(", ");

            if (joinedTitles.length > maxChars) {
                joinedTitles = joinedTitles.slice(0, maxChars - 3).trim() + "...";
            } else if (filterPayload.products.length > maxTitles) {
                joinedTitles += ", ...";
            }

            applied.push({
                key: "products",
                label: `${joinedTitles}`,
                onRemove: () =>
                    setFilterPayload((prev) => ({
                        ...prev,
                        products: [],
                    })),
            });
        }

        return applied;
    };
    const filters = [
        {
            key: "fileType",
            label: "File type",
            filter: (
                <ChoiceList
                    title="File type"
                    titleHidden
                    choices={[
                        { label: "Images", value: "Image", disabled: restrictToImages },
                        { label: "Videos", value: "Video", disabled: restrictToImages },
                        { label: "External videos", value: "ExternalVideo", disabled: restrictToImages },
                        { label: "3D Models", value: "Model3d", disabled: restrictToImages },
                    ]}
                    selected={filterPayload?.mediaType ?? []}
                    onChange={(value) => {
                        setFilterPayload((prev) => ({
                            ...prev,
                            mediaType: value as MediaTypeFilter[], // value is an array
                        }));
                    }}
                    allowMultiple
                />
            ),
            shortcut: true,
            pinned: true,
        },
        {
            key: "fileSize",
            label: "File size",
            filter: (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <TextField
                        label="Min size (MB)"
                        type="number"
                        value={filterPayload?.originalUploadSize?.min || ""}
                        onChange={(value) => {
                            setFilterPayload((prev) => ({
                                ...prev,
                                originalUploadSize: {
                                    ...prev.originalUploadSize,
                                    min: value || "",
                                },
                            }));
                        }}
                        autoComplete="off"
                    />
                    <TextField
                        label="Max size (MB)"
                        type="number"
                        value={filterPayload?.originalUploadSize?.max || ""}
                        onChange={(value) => {
                            setFilterPayload((prev) => ({
                                ...prev,
                                originalUploadSize: {
                                    ...prev.originalUploadSize,
                                    max: value || "",
                                },
                            }));
                        }}
                        autoComplete="off"
                    />
                </div>
            ),
            shortcut: true,
            pinned: true,
        },
        {
            key: "usedIn",
            label: "Used in",
            filter: (
                <ChoiceList
                    title="Used in"
                    titleHidden
                    choices={[
                        {
                            label: "Product media",
                            value: "product",
                            disabled: (filterPayload?.products?.length ?? 0) > 0,
                        },
                        { label: "Other media", value: "none", disabled: (filterPayload?.products?.length ?? 0) > 0 },
                    ]}
                    selected={filterPayload.usedIn ? [filterPayload.usedIn] : []}
                    onChange={(value: string[]) => {
                        setFilterPayload((prev) => ({
                            ...prev,
                            usedIn: value.length > 0 ? (value[0] as "product" | "none") : null, // set null if nothing selected
                        }));
                    }}
                    allowMultiple={false}
                />
            ),
            shortcut: true,
            pinned: true,
        },
        {
            key: "products",
            label: "product",
            filter: (
                <ProductsChoiceList
                    fetchMoreProducts={fetchMoreProducts}
                    filterPayload={filterPayload}
                    setFilterPayload={setFilterPayload}
                    productOptions={productOptions}
                    productHasNextPage={productHasNextPage}
                    productSearchTerm={productSearchTerm}
                    setProductSearchTerm={setProductSearchTerm}
                />
            ),
            shortcut: true,
            pinned: true,
        },
    ];

    const handleSearchInputChange = (value: string) => {
        setSearchInputValue(value);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            setFilterPayload((prev) => ({ ...prev, title: value }));
        }, 500);
    };

    // first effect , when modal opens → run both
    useEffect(() => {
        if (open) {
            handleFetchImages(); // no reset here
            fetchMoreProducts();
        }
    }, [open]);

    // second effect, when filterPayload changes only run handleFetchImages with reset
    useEffect(() => {
        if (open) {
            if (firstPageRender.current) {
                // skip first time, firstPageRender will reset in handleFetchImages
                return;
            }
            handleFetchImages(null, true);
        }
    }, [filterPayload]);

    console._log("filterpayload:", filterPayload);

    console._log("files:", files);

    return (
        <div className={styles.overrideModal}>
            <Modal
                open={open}
                onScrolledToBottom={() => handleFetchImages(cursor)}
                sectioned
                onClose={handleClose}
                title={title}
                noScroll
                instant
                limitHeight={false}
                size="large"
                primaryAction={{
                    content: selectButtonLabel,
                    onAction: handleSelect,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: handleClose,
                    },
                ]}
            >
                <div
                    style={{
                        minHeight: isMobile ? "75vh" : "60vh",
                    }}
                >
                    {fetchingFiles && firstPageRender.current ? (
                        renderSkeleton()
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {/* Header section */}
                            <div
                                style={{
                                    zIndex: 10,
                                    display: isMobile && previewFile ? "none" : "",
                                    background: "white",
                                    width: previewFile ? "50%" : "",
                                    position: previewFile ? "absolute" : "static",
                                    paddingRight: previewFile ? "10px" : "",
                                    // boxShadow:
                                    //     "0rem .5rem 1.5rem -.5rem rgba(0, 0, 0, .28), 0rem .5rem 1rem -.25rem rgba(0, 0, 0, .05), 0rem .1875rem .375rem 0rem rgba(0, 0, 0, .05), 0rem .125rem .25rem 0rem rgba(0, 0, 0, .05), 0rem .0625rem .125rem 0rem rgba(0, 0, 0, .05), 0rem 0rem 0rem .0625rem rgba(0, 0, 0, .06)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        marginBottom: 5,
                                    }}
                                >
                                    {/* Search bar */}
                                    <div style={{ flex: 1, position: "relative", maxWidth: "500px" }}>
                                        <TextField
                                            label="Search images"
                                            labelHidden
                                            prefix={<Icon source={SearchIcon} />}
                                            placeholder="Search files"
                                            value={searchInputValue}
                                            onChange={handleSearchInputChange}
                                            clearButton
                                            onClearButtonClick={() => {
                                                setFilterPayload((prev) => ({ ...prev, title: "" }));
                                            }}
                                            autoComplete="off"
                                            connectedRight={
                                                fetchingFiles || filtering ? <Spinner size="small" /> : null
                                            }
                                        />
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                        }}
                                    >
                                        {/* Sort with icon */}
                                        <Popover
                                            active={sortPopoverActive}
                                            activator={
                                                <Button onClick={toggleSortPopover} icon={SortIcon} disclosure>
                                                    Sort
                                                </Button>
                                            }
                                            onClose={toggleSortPopover}
                                        >
                                            <div
                                                style={{
                                                    minWidth: "14rem",
                                                }}
                                            >
                                                <ActionList
                                                    items={[
                                                        {
                                                            content: "Date added (newest first)",
                                                            active:
                                                                filterPayload?.sortKey === "ID" &&
                                                                filterPayload?.reverse === "true",
                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey === "ID" &&
                                                                        filterPayload?.reverse === "true"
                                                                    }
                                                                />
                                                            ),
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "ID",
                                                                    reverse: "true",
                                                                }));
                                                            },
                                                        },
                                                        {
                                                            active:
                                                                filterPayload?.sortKey === "ID" &&
                                                                filterPayload?.reverse === "false",
                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey === "ID" &&
                                                                        filterPayload?.reverse === "false"
                                                                    }
                                                                />
                                                            ),
                                                            content: "Date added (oldest first)",
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "ID",
                                                                    reverse: "false",
                                                                }));
                                                            },
                                                        },
                                                        {
                                                            active:
                                                                filterPayload?.sortKey === "FILENAME" &&
                                                                filterPayload?.reverse === "false",
                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey === "FILENAME" &&
                                                                        filterPayload?.reverse === "false"
                                                                    }
                                                                />
                                                            ),
                                                            content: "File name (A-Z)",
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "FILENAME",
                                                                    reverse: "false",
                                                                }));
                                                            },
                                                        },
                                                        {
                                                            active:
                                                                filterPayload?.sortKey === "FILENAME" &&
                                                                filterPayload?.reverse === "true",

                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey === "FILENAME" &&
                                                                        filterPayload?.reverse === "true"
                                                                    }
                                                                />
                                                            ),
                                                            content: "File name (Z-A)",
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "FILENAME",
                                                                    reverse: "true",
                                                                }));
                                                            },
                                                        },
                                                        {
                                                            active:
                                                                filterPayload?.sortKey === "ORIGINAL_UPLOAD_SIZE" &&
                                                                filterPayload?.reverse === "false",
                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey ===
                                                                            "ORIGINAL_UPLOAD_SIZE" &&
                                                                        filterPayload?.reverse === "false"
                                                                    }
                                                                />
                                                            ),
                                                            content: "File size (smallest first)",
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "ORIGINAL_UPLOAD_SIZE",
                                                                    reverse: "false",
                                                                }));
                                                            },
                                                        },
                                                        {
                                                            active:
                                                                filterPayload?.sortKey === "ORIGINAL_UPLOAD_SIZE" &&
                                                                filterPayload?.reverse === "true",
                                                            prefix: (
                                                                <RadioButton
                                                                    label=""
                                                                    name="sortOption"
                                                                    checked={
                                                                        filterPayload?.sortKey ===
                                                                            "ORIGINAL_UPLOAD_SIZE" &&
                                                                        filterPayload?.reverse === "true"
                                                                    }
                                                                />
                                                            ),
                                                            content: "File size (largest first)",
                                                            onAction: () => {
                                                                setFilterPayload((prev) => ({
                                                                    ...prev,
                                                                    sortKey: "ORIGINAL_UPLOAD_SIZE",
                                                                    reverse: "true",
                                                                }));
                                                            },
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </Popover>

                                        {/* Layout toggle as icons */}
                                        <Popover
                                            active={layoutTypePopoverActive}
                                            activator={
                                                <Button
                                                    onClick={toggleLayoutTypePopoverActive}
                                                    disclosure
                                                    icon={layoutType === "grid" ? TextInColumnsIcon : ListBulletedIcon}
                                                />
                                            }
                                            onClose={toggleLayoutTypePopoverActive}
                                        >
                                            <div
                                                style={{
                                                    minWidth: "9rem",
                                                }}
                                            >
                                                <ActionList
                                                    items={[
                                                        {
                                                            content: "Grid View",
                                                            icon: TextInColumnsIcon,
                                                            active: layoutType === "grid",
                                                            suffix: layoutType === "grid" && (
                                                                <Icon source={CheckIcon} />
                                                            ),
                                                            onAction: () => {
                                                                setLayoutType("grid");
                                                            },
                                                        },
                                                        {
                                                            content: "List View",
                                                            icon: ListBulletedIcon,
                                                            active: layoutType === "list",
                                                            suffix: layoutType === "list" && (
                                                                <Icon source={CheckIcon} />
                                                            ),
                                                            onAction: () => {
                                                                setLayoutType("list");
                                                            },
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </Popover>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        paddingBottom: "5px",
                                    }}
                                >
                                    <Filters
                                        hideQueryField={true}
                                        queryValue={undefined} // no search field
                                        onQueryChange={() => {}}
                                        onQueryClear={() => {}}
                                        filters={filters}
                                        borderlessQueryField
                                        appliedFilters={getAppliedFilters()}
                                        onClearAll={() => {
                                            setFilterPayload((prev) => ({
                                                ...prev,
                                                mediaType: [],
                                                productId: [],
                                                usedIn: null,
                                                originalUploadSize: { min: "", max: "" },
                                                products: [],
                                            }));
                                        }}
                                        closeOnChildOverlayClick={true}
                                    />
                                </div>
                            </div>

                            {/* Main Content */}
                            <div style={{ flex: 1, position: "relative" }}>
                                {files.length == 0 ? (
                                    // Empty state
                                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                                        <Icon source={SearchIcon} tone="subdued" />

                                        <p style={{ marginTop: 10, fontWeight: 500 }}>No results found</p>
                                        <p style={{ marginTop: 5, marginBottom: 10, color: "#999" }}>
                                            Edit your search criteria, or upload a new file.
                                        </p>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                /* trigger upload */
                                            }}
                                        >
                                            Upload file
                                        </Button>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 16,
                                            position: "relative",
                                        }}
                                        ref={scrollContainer}
                                    >
                                        {/* Left Panel */}
                                        <div
                                            style={{
                                                flex: isMobile && previewFile ? "0" : "1", // hide when preview visible on mobile
                                                display: isMobile && previewFile ? "none" : "block",
                                                marginTop: previewFile ? "84px" : "5px",
                                                transition: "flex 0.3s",
                                                overflowY: "scroll",
                                                scrollbarWidth: "none",
                                                height: isMobile ? "" : "calc(-120px + 70vh)",
                                            }}
                                        >
                                            <div>
                                                {/* Dropzone */}
                                                <div>{/* your dropzone component */}</div>
                                                {/* files Grid */}
                                                <div
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 140px))",
                                                        justifyContent: "start",
                                                        gap: 12,
                                                    }}
                                                >
                                                    {files.map((file, i) => (
                                                        <FileCard
                                                            key={i}
                                                            file={file}
                                                            onPreview={handlePreview}
                                                            checked={isChecked(file.id)}
                                                            onToggleChecked={(previewUrl) =>
                                                                handleToggleChecked(file.id, previewUrl)
                                                            }
                                                            multiple={multiple}
                                                        />
                                                    ))}
                                                </div>

                                                {filesHasNextPage && (
                                                    <span
                                                        ref={loadMoreTriggerRef}
                                                        style={{
                                                            width: "100%",
                                                            display: "flex",
                                                            height: "5px",
                                                            // backgroundColor: "red",
                                                            visibility: "hidden",
                                                        }}
                                                    />
                                                )}
                                                {/* Spinner at end when fetchingFiles */}
                                                {fetchingFiles && (
                                                    <div
                                                        style={{
                                                            gridColumn: "1 / -1", // take full row
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            padding: "16px 0",
                                                        }}
                                                    >
                                                        <Spinner accessibilityLabel="Loading files" size="small" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right Panel */}
                                        {previewFile && (
                                            <div
                                                style={{
                                                    flex: isMobile ? "1" : "1", //borderLeft: !isMobile ? "1px solid #eee" : "none",
                                                    paddingLeft: !isMobile ? 16 : 0,
                                                    overflow: previewFile ? "auto" : "hidden",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <>
                                                    {/* Top Bar */}
                                                    <div
                                                        style={{
                                                            background: "#fafafa",
                                                            zIndex: 2,
                                                            padding: "5px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: "600", fontSize: 12 }}>Preview</div>
                                                        <div
                                                            style={{
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() => setPreviewFile(null)} // close
                                                        >
                                                            <Icon source={XCircleIcon} />{" "}
                                                        </div>
                                                    </div>

                                                    {/* Image Preview Section */}
                                                    <div
                                                        style={{
                                                            position: "relative",

                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            background: "#fafafa",
                                                            height: "100%",
                                                        }}
                                                    >
                                                        {/* Left Arrow */}
                                                        {/* <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                left: "0",
                                                            }}
                                                            onClick={() => {}}
                                                        >
                                                            <Icon source={ArrowLeftIcon} />
                                                        </div> */}

                                                        <div
                                                            style={{
                                                                aspectRatio: "1",
                                                                position: "relative",
                                                                borderRadius: 8,
                                                                overflow: "hidden",
                                                                display: "flex",
                                                                minWidth: "235px",
                                                            }}
                                                        >
                                                            {/* Image */}
                                                            <img
                                                                src={previewFile.biggerPreviewUrl}
                                                                alt={previewFile.fileName}
                                                                style={{
                                                                    width: "100%",
                                                                    maxWidth: isMobile ? "470px" : "350px",
                                                                    objectFit: "contain",
                                                                    transition: "filter 0.1s ease",
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Right Arrow */}
                                                        {/* <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                right: "0",
                                                            }}
                                                            onClick={() => {}}
                                                        >
                                                            <Icon source={ArrowRightIcon} />
                                                        </div> */}
                                                    </div>

                                                    {/* Info Section */}
                                                    <div
                                                        style={{
                                                            padding: "10px 15px",
                                                            background: "#fafafa",
                                                            position: "absolute",
                                                            bottom: "0",
                                                        }}
                                                    >
                                                        {/* File Name */}
                                                        <div
                                                            style={{
                                                                fontWeight: "600",
                                                                fontSize: 12,
                                                                marginBottom: 8,
                                                            }}
                                                        >
                                                            {previewFile.fileName}
                                                        </div>

                                                        {/* Metadata */}
                                                        <div
                                                            style={{
                                                                color: "#666",
                                                                fontSize: 10,
                                                                marginBottom: 5,
                                                                background: "#fafafa",
                                                            }}
                                                        >
                                                            {new Date(previewFile.file.createdAt).toLocaleDateString()}{" "}
                                                            • {previewFile.fileType}{" "}
                                                            {previewFile.file.__typename === "MediaImage" &&
                                                                previewFile.file.preview?.image?.width &&
                                                                previewFile.file.preview?.image?.height && (
                                                                    <>
                                                                        • {previewFile.file.preview.image.width}x
                                                                        {previewFile.file.preview.image.height}
                                                                    </>
                                                                )}
                                                        </div>

                                                        {/* Alt Text */}
                                                        {previewFile.file.alt && (
                                                            <div
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: "#333",

                                                                    lineHeight: "1.4",
                                                                }}
                                                            >
                                                                {previewFile.file.alt}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default MediaLibrary;

// FileCard component
const FileCard = ({
    file,
    onPreview,
    checked,
    onToggleChecked,
    multiple,
}: {
    file: FileItem;
    onPreview: ({ file, fileName, fileType, biggerPreviewUrl }: Preview) => void;
    checked: boolean;
    onToggleChecked: (previewUrl: string) => void;
    multiple: boolean;
}) => {
    const [hovered, setHovered] = useState(false);

    const extractNameFromPreviewUrl = (url: string) => {
        const match = url.match(
            /\/([^\/]+)\.(jfif|pjp|jpg|pjpeg|jpeg|gif|png|heic|webp|svgz|svg|m4v|mp4|mov|webm|glb|usdz|gltf)/i,
        );
        if (match) {
            return match[1] + "." + match[2];
        }
        return "";
    };

    let fileName = "";
    let fileType = "";
    let previewUrl = "";
    let biggerPreviewUrl = "";

    if (file.__typename === "MediaImage") {
        previewUrl = file.preview.image?.transformedSrc || "";
        biggerPreviewUrl = file.preview.image?.url || "";
        fileName = extractNameFromPreviewUrl(previewUrl);
        fileType = previewUrl.split(".").pop()?.split("?")[0] || "";
    } else if (file.__typename === "Video") {
        previewUrl = file.preview.image?.transformedSrc || "";
        biggerPreviewUrl = file.preview.image?.url || "";
        fileName = file.filename || "";
        fileType = file.originalSource?.format || "";
    } else if (file.__typename === "Model3d") {
        previewUrl = file.preview.image?.transformedSrc || "";
        biggerPreviewUrl = file.preview.image?.url || "";

        const originalUrl = file.originalSource?.url || "";
        fileName = originalUrl.split("/").pop()?.split("?")[0] || "";
        fileType = originalUrl.split(".").pop()?.split("?")[0] || "";
    } else if (file.__typename === "ExternalVideo") {
        previewUrl = file.preview.image?.transformedSrc || "";
        biggerPreviewUrl = file.preview.image?.url || "";
        fileName = extractNameFromPreviewUrl(previewUrl);
        fileType = "";
    }

    const formatDuration = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = (totalSeconds % 60).toString().padStart(2, "0");

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds}`;
        }
        return `${minutes}:${seconds}`;
    };

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 8,
                borderRadius: 8,
                backgroundColor: checked ? "#f1f1f1" : hovered ? "#f9fafb" : "white",
                cursor: "pointer",
                width: 140,
                gap: 8,
                position: "relative",
            }}
        >
            {/* THUMBNAIL wrapper → make this relative */}
            <div
                style={{
                    width: "90%",
                    aspectRatio: "1",
                    position: "relative",
                    borderRadius: 8,
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                onClick={() => {
                    onToggleChecked(previewUrl);
                }}
            >
                {/* actual image */}
                <img
                    src={previewUrl}
                    alt={fileName}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        filter: hovered ? "brightness(0.7)" : "none",
                        transition: "filter 0.1s ease",
                    }}
                />

                {/* Checkbox on LEFT */}
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,

                        borderRadius: 4,
                        padding: 1,
                        zIndex: 2,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleChecked(previewUrl);
                    }}
                >
                    <Checkbox label labelHidden checked={checked} onChange={() => {}} />
                </div>

                {/* Video duration on RIGHT */}
                {file.__typename === "Video" && (
                    <div
                        style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            background: "rgba(0,0,0,0.7)",
                            color: "white",
                            fontSize: 12,
                            padding: "2px 4px",
                            borderRadius: 4,
                        }}
                    >
                        {formatDuration(file.duration)}
                    </div>
                )}

                {/* Eye icon bottom right */}
                {hovered && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: 1,
                            right: 2,
                            background: "rgba(5, 93, 181, 0.95)", // bluish bg
                            borderRadius: 4,
                            padding: 1,
                            cursor: "pointer",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreview({ file, fileName, fileType, biggerPreviewUrl });
                        }}
                    >
                        <div
                            style={{
                                filter: "invert(1)", // makes icon white
                                width: 16,
                                height: 16,
                            }}
                        >
                            <Icon source={ViewIcon} />
                        </div>
                    </div>
                )}
            </div>

            {/* File name */}
            <div
                style={{
                    width: "100%",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                title={fileName}
            >
                {fileName}
            </div>

            {/* File type */}
            <div style={{ fontSize: 12, color: "#888" }}>{fileType.toUpperCase()}</div>
        </div>
    );
};

const ProductsChoiceList = ({
    filterPayload,
    setFilterPayload,
    productOptions,
    fetchMoreProducts,
    productHasNextPage,
    productSearchTerm,
    setProductSearchTerm,
}: {
    filterPayload: FilterPayload;
    setFilterPayload: React.Dispatch<React.SetStateAction<FilterPayload>>;
    productOptions: ProductOption[];
    fetchMoreProducts: (searchTerm?: string) => Promise<void>;
    productHasNextPage: boolean;
    productSearchTerm: string;
    setProductSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}) => {
    // const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
    const [selected, setSelected] = useState<ProductOption[]>(filterPayload.products ?? []);

    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const debounceTimer = useRef<any>(null);

    const handleChange = (value: string[]) => {
        // value is array of product ids , so we map back to [{id, title}]
        const selectedProducts = productOptions
            .filter((p) => value.includes(p.id))
            .map((p) => ({ id: p.id, title: p.title }));

        setSelected(selectedProducts);

        setFilterPayload((prev) => ({
            ...prev,
            products: selectedProducts,
            usedIn: null,
        }));
    };

    const handleSearchChange = (value: string, showMore: boolean) => {
        setProductSearchTerm(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(async () => {
            if (showMore) {
                setLoading(true);
            } else {
                setSearchLoading(true);
            }
            try {
                await fetchMoreProducts(value);
            } finally {
                setLoading(false);
                setSearchLoading(false);
            }
        }, 500); // 500ms delay
    };

    const newProductOptions = productOptions?.map((product) => ({ label: product?.title, value: product?.id }));
    const selectedOrder = selected.map((sel) => sel.id);

    const formattedOptions = newProductOptions.sort((a, b) => {
        const aIndex = selectedOrder.indexOf(a.value);
        const bIndex = selectedOrder.indexOf(b.value);

        const aSelected = aIndex !== -1;
        const bSelected = bIndex !== -1;

        if (aSelected && bSelected) {
            // both selected preserve order of selection
            return aIndex - bIndex;
        } else if (aSelected) {
            // a selected go on top
            return -1;
        } else if (bSelected) {
            // b selected go on top
            return 1;
        } else {
            // neither selected, keep original order
            return 0;
        }
    });

    return (
        <div style={{ minWidth: "250px" }}>
            <TextField
                label=""
                value={productSearchTerm}
                onChange={(value) => handleSearchChange(value, false)}
                placeholder="Search products"
                autoComplete="off"
                clearButton
                autoFocus={true}
                size="slim"
                loading={loading || searchLoading}
                onClearButtonClick={() => handleSearchChange("", false)}
            />
            {/* If no products found */}
            {searchLoading ? (
                <div style={{ padding: "16px", textAlign: "center" }}>
                    <Spinner size="small" />
                </div>
            ) : formattedOptions.length === 0 ? (
                <div style={{ padding: "8px" }}>No products found</div>
            ) : (
                <>
                    <ChoiceList
                        title="Products"
                        titleHidden
                        choices={formattedOptions}
                        selected={selected.map((p) => p.id)}
                        onChange={handleChange}
                        allowMultiple
                    />
                    {productHasNextPage && (
                        <Button
                            onClick={() => handleSearchChange(productSearchTerm, true)}
                            loading={loading}
                            disabled={loading || searchLoading}
                            variant="plain"
                        >
                            Show more products
                        </Button>
                    )}
                </>
            )}
        </div>
    );
};

const renderSkeleton = () => (
    <div style={{ padding: 16 }}>
        {/* Skeleton for Top Bar (Search + Sort + Layout buttons) */}
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
            }}
        >
            {/* Search Bar */}
            <div
                style={{
                    width: "100%",
                    maxWidth: 500,
                    height: 32,
                    backgroundColor: "#eee",
                    borderRadius: 6,
                }}
            ></div>

            {/* Sort + Layout buttons */}
            <div style={{ display: "flex", gap: 12 }}>
                <div style={{ width: 80, height: 32, backgroundColor: "#eee", borderRadius: 6 }}></div>
                <div style={{ width: 40, height: 32, backgroundColor: "#eee", borderRadius: 6 }}></div>
            </div>
        </div>

        {/* Skeleton for Filters row */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={{ width: 50, height: 16, backgroundColor: "#eee", borderRadius: 10 }}></div>
            <div style={{ width: 50, height: 16, backgroundColor: "#eee", borderRadius: 10 }}></div>
            <div style={{ width: 50, height: 16, backgroundColor: "#eee", borderRadius: 10 }}></div>
        </div>

        {/* Skeleton for Dropzone */}
        {/* <div
            style={{
                width: "100%",
                height: 160,
                backgroundColor: "#f5f5f5",
                border: "2px dashed #ccc",
                borderRadius: 8,
                marginBottom: 24,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "16px",
            }}
        >

            <Button disabled>Media</Button>

         
            <div
                style={{
                    color: "#888",
                    fontSize: "12px",
                    marginTop: "8px",
                    textAlign: "center",
                }}
            >
                Drag and drop images, videos, 3D models, and files
            </div>
        </div> */}

        {/* Skeleton grid for images */}
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: 16,
                justifyItems: "center",
            }}
        >
            {Array(12)
                .fill(0)
                .map((_, i) => (
                    <div
                        key={i}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 8,
                            width: 120,
                        }}
                    >
                        {/* Image skeleton */}
                        <div
                            style={{
                                width: 100,
                                height: 100,
                                backgroundColor: "#eee",
                                borderRadius: 8,
                            }}
                        ></div>

                        {/* Display name skeleton */}
                        <div
                            style={{
                                width: "80%",
                                height: 12,
                                backgroundColor: "#eee",
                                borderRadius: 4,
                            }}
                        ></div>
                    </div>
                ))}
        </div>
    </div>
);
